import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import { useSelector } from "react-redux"

import { ColumnSchema, ColumnTypeEnum, MasterSchemaQuery } from "@/codegen/graphql-request"
import { RootState } from "@/store"
import { ColumnTypes, ReferenceConfig } from "@/type"

export type MasterSchemaState = {
	data: MasterSchemaQuery["masterSchema"]
	currentTableName: string | undefined
}

export const initialState: MasterSchemaState = {
	data: [],
	currentTableName: undefined,
}

export const masterSchemaSlice = createSlice({
	name: "masterSchema",
	initialState,
	reducers: {
		setData: (state, action: PayloadAction<MasterSchemaQuery>) => {
			state.data = action.payload.masterSchema
			if (!state.currentTableName && action.payload.masterSchema[0]) {
				state.currentTableName = action.payload.masterSchema[0].tableName
			}
		},
		setCurrentTableName: (state, action: PayloadAction<string>) => {
			state.currentTableName = action.payload
		},
		setDisplayWidth: (
			state,
			action: PayloadAction<{ tableName: string; columnName: string; displayWidth: number }>,
		) => {
			const { tableName, columnName, displayWidth } = action.payload
			const tableSchema = state.data.find((s) => s.tableName == tableName)
			if (!tableSchema) throw `not found table schema ${tableName}`
			const columnSchema = tableSchema.columns.find((c) => c.name == columnName)
			if (!columnSchema) throw `not found column schema ${columnName}`
			columnSchema.displayWidth = displayWidth
		},
		setRefDisplay: (state, action: PayloadAction<{ tableName: string; columnName: string; refDisplay: string }>) => {
			const { tableName, columnName, refDisplay } = action.payload
			const tableSchema = state.data.find((s) => s.tableName == tableName)
			if (!tableSchema) throw `not found table schema ${tableName}`
			const columnSchema = tableSchema.columns.find((c) => c.name == columnName)
			if (!columnSchema) throw `not found column schema ${columnName}`
			columnSchema.refDisplay = refDisplay
		},
		setDisplayName: (state, action: PayloadAction<{ tableName: string; columnName: string; displayName: string }>) => {
			const { tableName, columnName, displayName } = action.payload
			const tableSchema = state.data.find((s) => s.tableName == tableName)
			if (!tableSchema) throw `not found table schema ${tableName}`
			const columnSchema = tableSchema.columns.find((c) => c.name == columnName)
			if (!columnSchema) throw `not found column schema ${columnName}`
			columnSchema.displayName = displayName
		},
		setDescription: (state, action: PayloadAction<{ tableName: string; columnName: string; description: string }>) => {
			const { tableName, columnName, description } = action.payload
			const tableSchema = state.data.find((s) => s.tableName == tableName)
			if (!tableSchema) throw `not found table schema ${tableName}`
			const columnSchema = tableSchema.columns.find((c) => c.name == columnName)
			if (!columnSchema) throw `not found column schema ${columnName}`
			columnSchema.description = description
		},
		changeColumnOrder: (state, action: PayloadAction<{ tableName: string; newColumnNames: string[] }>) => {
			const { tableName, newColumnNames } = action.payload
			const tableSchema = state.data.find((s) => s.tableName == tableName)
			if (!tableSchema) throw `not found table schema ${tableName}`

			const newColumns = []
			let i = 0
			for (const columnName of newColumnNames) {
				const column = tableSchema.columns.find((column) => column.name == columnName)
				if (column) {
					column.sortIndex = i
					newColumns.push(column)
					i = i + 1
				}
			}
			tableSchema.columns = newColumns
		},
	},
})

export const useReferenceConfigs = (tableName: string): { [columnName: string]: ReferenceConfig } => {
	return useSelector((state: RootState) => getReferenceConfigs(state, tableName))
}
export const getReferenceConfigs = (state: RootState, tableName: string) => {
	const tableScheme = state.masterSchema.data.find((s) => s.tableName == tableName)
	if (!tableScheme) throw `not found table schema ${tableName}`

	const referenceConfigs: { [columnName: string]: ReferenceConfig } = {}
	tableScheme.columns
		.filter((columnSchema) => columnSchema.ref)
		.forEach((columnSchema) => {
			const refTableSchema = state.masterSchema.data.find((s) => s.tableName == columnSchema.ref)
			if (refTableSchema) {
				referenceConfigs[columnSchema.name] = {
					tableName: refTableSchema.tableName,
					displayColumn: columnSchema.refDisplay ?? refTableSchema.primaryKey,
					pkColumn: refTableSchema.primaryKey,
				}
			}
		})

	return referenceConfigs
}

export const useColumnTypes = () => {
	return useSelector((state: RootState) => getColumnTypes(state))
}
export const getColumnTypes = (state: RootState) => {
	const tableColumnTypes: { [tableName: string]: ColumnTypes } = {}
	for (const tableSchema of state.masterSchema.data) {
		const columnTypes: ColumnTypes = {}
		tableSchema.columns.forEach((columnSchema) => {
			columnTypes[columnSchema.name] = columnSchema?.type || ColumnTypeEnum.String
		})
		tableColumnTypes[tableSchema.tableName] = columnTypes
	}
	return tableColumnTypes
}

export const useColumnSchemas = (tableName: string) => {
	return useSelector((state: RootState) => {
		const tableSchema = state.masterSchema.data.find((s) => s.tableName == tableName)
		if (!tableSchema) throw `not found table schema ${tableName}`
		const columnSchemas: { [columnName: string]: ColumnSchema } = {}
		tableSchema.columns.forEach((columnSchema) => {
			columnSchemas[columnSchema.name] = columnSchema
		})
		return columnSchemas
	})
}

export const useTableSchema = (tableName?: string) => {
	return useSelector((state: RootState) => {
		return state.masterSchema.data.find((s) => s.tableName == tableName)
	})
}
